// หาสี่เหลี่ยมมุมกระดาษในภาพจากกล้อง — กระดาษไม่ได้เต็มภาพ มีโต๊ะ/ของอื่นรอบๆ ถ่ายเฉียง และแสงไม่เท่ากัน
//
// ต่างจาก findMarkers (ภาพสแกนเต็มหน้า) สามข้อ
//   1. threshold แบบปรับตามพื้นที่ (Bradley) — เงามือถือทำให้ค่าเดียวทั้งภาพแยกหมึกกับกระดาษไม่ได้
//   2. ค้นทั้งภาพ แล้วกรองเฉพาะ "สี่เหลี่ยมจัตุรัสทึบ" ด้วยสัดส่วน พื้นที่ / (2 x ระยะไกลสุดจากศูนย์กลาง²)
//      ซึ่งไม่ขึ้นกับการหมุน: จัตุรัส ≈ 1.0, วงกลม ≈ 1.57 (ตราโรงเรียน ช่องที่ฝน), สี่เหลี่ยมผืนผ้า < 0.8
//   3. เลือก 4 ก้อนขนาดใกล้กันที่อยู่สุดมุม แล้วตรวจว่าเป็นรูปสี่เหลี่ยมที่สัดส่วนใกล้แบบกระดาษจริง (เต็มหน้า/ครึ่งแผ่น)
// ผลผิดไม่อันตราย — ถ้าเลือกผิด 4 จุด แถบรหัสจะถอดไม่ผ่าน CRC และแผ่นนั้นถูกข้าม

// สัดส่วน สูง/กว้าง ของระยะระหว่างศูนย์กลางสี่เหลี่ยมมุม ของทุกแบบที่ตัวอ่านรู้จัก
// — เต็มหน้าราว 1.4–1.6, ครึ่งแผ่น (แบบ 5) ราว 0.67 ตัวตัดสินจริงคือ CRC ของแถบรหัส
import { LAYOUT_VERSIONS, markersFor } from '../sheetLayout'
const SPAN_RATIOS = [...new Set(LAYOUT_VERSIONS.map((v) => {
  const ms = markersFor(v)
  const at = (c) => ms.find((m) => m.corner === c)
  return (at('bl').y - at('tl').y) / (at('tr').x - at('tl').x)
}))]

const adaptiveDark = (img) => {
  const { width: W, height: H, data } = img
  const integral = new Float64Array((W + 1) * (H + 1))
  for (let y = 0; y < H; y++) {
    let row = 0
    for (let x = 0; x < W; x++) {
      row += data[y * W + x]
      integral[(y + 1) * (W + 1) + x + 1] = integral[y * (W + 1) + x + 1] + row
    }
  }
  const half = Math.max(4, Math.round(Math.min(W, H) / 16))
  const dark = new Uint8Array(W * H)
  for (let y = 0; y < H; y++) {
    const y0 = Math.max(0, y - half)
    const y1 = Math.min(H, y + half + 1)
    for (let x = 0; x < W; x++) {
      const x0 = Math.max(0, x - half)
      const x1 = Math.min(W, x + half + 1)
      const sum = integral[y1 * (W + 1) + x1] - integral[y0 * (W + 1) + x1] - integral[y1 * (W + 1) + x0] + integral[y0 * (W + 1) + x0]
      const count = (x1 - x0) * (y1 - y0)
      // มืดกว่าค่าเฉลี่ยรอบข้าง 25% และมืดจริง (ไม่ใช่แค่ต่างจากพื้นขาวจัดนิดหน่อย)
      if (data[y * W + x] * count < sum * 0.75 && data[y * W + x] < 170) dark[y * W + x] = 1
    }
  }
  return dark
}

const components = (img, dark, minArea, maxArea) => {
  const { width: W, height: H } = img
  const seen = new Uint8Array(W * H)
  const stack = []
  const pix = []
  const out = []
  for (let start = 0; start < W * H; start++) {
    if (!dark[start] || seen[start]) continue
    seen[start] = 1
    stack.push(start)
    pix.length = 0
    let sx = 0; let sy = 0
    while (stack.length) {
      const p = stack.pop()
      pix.push(p)
      const x = p % W
      const y = (p - x) / W
      sx += x; sy += y
      if (x > 0 && dark[p - 1] && !seen[p - 1]) { seen[p - 1] = 1; stack.push(p - 1) }
      if (x < W - 1 && dark[p + 1] && !seen[p + 1]) { seen[p + 1] = 1; stack.push(p + 1) }
      if (y > 0 && dark[p - W] && !seen[p - W]) { seen[p - W] = 1; stack.push(p - W) }
      if (y < H - 1 && dark[p + W] && !seen[p + W]) { seen[p + W] = 1; stack.push(p + W) }
    }
    const n = pix.length
    if (n < minArea || n > maxArea) continue
    const cx = sx / n
    const cy = sy / n
    let maxD2 = 0; let xx = 0; let yy = 0; let xy = 0
    for (const p of pix) {
      const dx = (p % W) - cx
      const dy = Math.floor(p / W) - cy
      const d2 = dx * dx + dy * dy
      if (d2 > maxD2) maxD2 = d2
      xx += dx * dx; yy += dy * dy; xy += dx * dy
    }
    // ระยะวัดจากศูนย์กลางพิกเซล จึงสั้นกว่ามุมจริงราวครึ่งพิกเซล — สี่เหลี่ยมเล็ก 7x7 พิกเซลจะถูกตัดทิ้งถ้าไม่ชดเชย
    const maxD = Math.sqrt(maxD2) + 0.7
    const squareness = n / (2 * maxD * maxD)
    // ความยืดจากโมเมนต์อันดับสอง: จัตุรัส/วงกลม = 1, ผืนผ้า 2:1 = 4
    const tr = (xx + yy) / n
    const det = (xx * yy - xy * xy) / (n * n)
    const disc = Math.sqrt(Math.max(0, (tr * tr) / 4 - det))
    const elong = (tr / 2 + disc) / Math.max(1e-9, tr / 2 - disc)
    if (squareness < 0.72 || squareness > 1.3 || elong > 1.8) continue
    // ความสว่างรอบๆ — สี่เหลี่ยมมุมจริงอยู่บนกระดาษขาว ของบนโต๊ะส่วนใหญ่ไม่ใช่ ใช้จัดลำดับ quad ที่จะลองก่อน
    let ring = 0
    for (let k = 0; k < 12; k++) {
      const a = (k / 12) * 2 * Math.PI
      const rx = Math.round(cx + Math.cos(a) * maxD * 1.6)
      const ry = Math.round(cy + Math.sin(a) * maxD * 1.6)
      ring += rx >= 0 && ry >= 0 && rx < W && ry < H ? img.data[ry * W + rx] : 0
    }
    out.push({ x: cx + 0.5, y: cy + 0.5, size: Math.sqrt(n), ring: ring / 12 })
  }
  return out
}

const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x)
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y)

// quad = [บนซ้าย, บนขวา, ล่างขวา, ล่างซ้าย] ในภาพ — ต้องนูน ใหญ่พอ และสัดส่วนใกล้แบบกระดาษ (ยอมให้ถ่ายเฉียง)
const plausible = (quad, W, H) => {
  const [a, b, c, d] = quad
  if (new Set(quad).size < 4) return false
  const turns = [cross(a, b, c), cross(b, c, d), cross(c, d, a), cross(d, a, b)]
  if (!(turns.every((v) => v > 0) || turns.every((v) => v < 0))) return false
  const top = dist(a, b); const bottom = dist(d, c)
  const left = dist(a, d); const right = dist(b, c)
  if (Math.max(top, bottom) / Math.min(top, bottom) > 1.7) return false
  if (Math.max(left, right) / Math.min(left, right) > 1.7) return false
  const ratio = (left + right) / (top + bottom)
  // ตั้ง (ภาพแนวตั้ง) หรือตะแคง (ภาพแนวนอน) ก็ได้ ทิศจริงตัดสินด้วยแถบรหัสทีหลัง
  const near = (r, want) => r > want * 0.65 && r < want * 1.45
  if (!SPAN_RATIOS.some((want) => near(ratio, want) || near(1 / ratio, want))) return false
  const area = Math.abs(turns[0] + turns[2]) / 2
  return area > W * H * 0.08
}

// img = ภาพความสว่างที่ย่อแล้ว (กว้างราว 640)
// คืนรายการ quad ที่เป็นไปได้ [บนซ้าย, บนขวา, ล่างขวา, ล่างซ้าย] เรียงจากน่าจะใช่ที่สุด — ผู้เรียกลองทีละตัว
// จนแถบรหัสถอดผ่าน. ที่ต้องมีหลายตัว: ของทึบบนโต๊ะใกล้มุมภาพ (ปุ่มคีย์บอร์ด ปกสมุด) ชนะ "สุดมุม" ได้
// จึงลองตัดทีละมุมที่เลือกไว้แล้วเลือกใหม่ด้วย
export const findMarkerQuads = (img) => {
  const { width: W, height: H } = img
  const short = Math.min(W, H)
  // สี่เหลี่ยมมุมกว้าง 7 มม. บนกระดาษกว้าง 210 มม. — กระดาษกินภาพตั้งแต่ราว 30% ถึงเต็มภาพ
  const minSide = short * 0.3 * (7 / 210) * 0.6
  const maxSide = Math.max(W, H) * (7 / 210) * 1.6
  const dark = adaptiveDark(img)
  const cands = components(img, dark, Math.max(9, minSide * minSide * 0.6), maxSide * maxSide)
  if (cands.length < 4) return []

  // แต่ละมุมเอา 3 ก้อนที่อยู่สุดมุมนั้นที่สุด แล้วลองทุกชุด (81 ชุด) — ของบนโต๊ะอาจกินมุมได้หลายมุมพร้อมกัน
  const scores = [(c) => -(c.x + c.y), (c) => c.x - c.y, (c) => c.x + c.y, (c) => c.y - c.x]
  const found = []
  const seen = new Set()
  const sizes = [...new Set(cands.map((c) => Math.round(c.size)))].sort((a, b) => b - a)
  for (const s of sizes.slice(0, 8)) {
    const group = cands.filter((c) => c.size >= s * 0.6 && c.size <= s * 1.6)
    if (group.length < 4) continue
    const tops = scores.map((score) => [...group].sort((a, b) => score(b) - score(a)).slice(0, 3))
    for (const a of tops[0]) for (const b of tops[1]) for (const c of tops[2]) for (const d of tops[3]) {
      const quad = [a, b, c, d]
      const key = quad.map((q) => `${Math.round(q.x)},${Math.round(q.y)}`).join('|')
      if (seen.has(key) || !plausible(quad, W, H)) continue
      seen.add(key)
      const sz = quad.map((q) => q.size)
      found.push({
        quad,
        mean: sz.reduce((t, v) => t + v, 0) / 4,
        // กระดาษรอบๆ สว่าง + สี่มุมขนาดใกล้กัน = น่าจะใช่ก่อน
        rank: quad.reduce((t, q) => t + q.ring, 0) / 4 - 40 * (Math.max(...sz) / Math.min(...sz) - 1),
      })
    }
  }
  // สี่เหลี่ยมมุมเป็นก้อนทึบที่ใหญ่ที่สุดบนกระดาษ — ช่องแถบรหัสขอบกระดาษ (แบบ 4–5) กับช่องที่ฝนขนาดราว 4 มม.
  // อยู่ใกล้มุมกระดาษและต่อกันเป็นรูปสี่เหลี่ยมที่สัดส่วนผ่านได้ ถ้าเงาพาดมุมล่าง (ความสว่างรอบมุมจริงตก)
  // quad ของก้อนเล็กจะแซงมุมจริงจนหลุด 12 อันดับ จึงให้คะแนนตามขนาดเทียบกับ quad ที่ใหญ่ที่สุดด้วย
  // (ของบนโต๊ะที่ใหญ่กว่ายังแพ้ เพราะรอบๆ เป็นโต๊ะมืด ต่างจากกระดาษมากกว่าที่คะแนนขนาดชดเชยได้)
  const biggest = Math.max(...found.map((f) => f.mean), 1)
  for (const f of found) f.rank += 100 * (f.mean / biggest)
  return found
    .sort((x, y) => y.rank - x.rank)
    .slice(0, 12)
    .map(({ quad }) => quad.map((c) => ({ x: c.x, y: c.y, size: c.size })))
}

// ย่อภาพความสว่างด้วยการเฉลี่ยเป็นบล็อก — หาสี่เหลี่ยมมุมบนภาพเล็ก แล้วค่อยอ่านช่องฝนบนภาพเต็ม
export const downscale = (img, targetLong = 640) => {
  const k = Math.max(1, Math.floor(Math.max(img.width, img.height) / targetLong))
  if (k === 1) return { img, k }
  const W = Math.floor(img.width / k)
  const H = Math.floor(img.height / k)
  const data = new Uint8ClampedArray(W * H)
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      let sum = 0
      for (let j = 0; j < k; j++) {
        const row = (y * k + j) * img.width + x * k
        for (let i = 0; i < k; i++) sum += img.data[row + i]
      }
      data[y * W + x] = sum / (k * k)
    }
  }
  return { img: { width: W, height: H, data }, k }
}
